import { cloneDeep, groupBy, orderBy } from "lodash";

import { CreativeMetricType } from "@north-beam/nb-client-side";
import { type SalesPageMetric } from "@north-beam/nb-client-side";

export enum MetricCategory {
  ALL = "All",
  NORTHBEAM = "Northbeam",
  FORECASTED_NORTHBEAM = "Forecasted Northbeam",
  USER_DEFINED = "Custom metrics",
  LTV = "LTV",
  CUSTOM_GOALS = "Custom goals",
  SUBSCRIPTION = "Subscriptions",
  FACEBOOK = "Facebook reported",
  TIK_TOK = "TikTok reported",
  GOOGLE = "Google reported",
  PLATFORM_BENCHMARKS = "Platform Benchmarks",
  YOU_TUBE = "YouTube",
  SNAPCHAT = "Snapchat reported",
  PINTEREST = "Pinterest reported",
  TWITTER = "Twitter reported",
  TRADEDESK = "Trade Desk reported",
}

type MetricCategories = Record<MetricCategory, SalesPageMetricWithChildren[]>;

export type SalesPageMetricWithChildren = SalesPageMetric & {
  children?: SalesPageMetric[];
};

export class MetricCategorizer {
  readonly #metrics: SalesPageMetric[];
  readonly #metricCategories: MetricCategories;
  readonly #metricCategoriesWithoutChildren: MetricCategories;

  constructor(metrics: SalesPageMetric[]) {
    this.#metrics = cloneDeep(metrics);
    this.#metricCategories = this.#groupMetricsByCategory();
    this.#metricCategoriesWithoutChildren = this.#groupMetricsByCategory(false);
  }

  public getMetricsCategories() {
    return this.#metricCategories;
  }

  public getMetricsByCategory(category: MetricCategory) {
    return this.#metricCategories[category] ?? [];
  }

  public getMetricsByCategoryUngrouped(category: MetricCategory) {
    return this.#metricCategoriesWithoutChildren[category] ?? [];
  }

  #groupMetricsByCategory(groupLikeMetrics = true) {
    const groupedByCategoryMetrics = groupBy(this.#metrics, (metric) => {
      // Defaults to Northbeam if no category is matched
      const categorizers = [
        this.#customGoalCategory(metric),
        this.#platformBenchmarksCategory(metric),
        this.#snapchatCategory(metric),
        this.#userDefinedCategory(metric),
        this.#youTubeCategory(metric),
        this.#googleCategory(metric),
        this.#pinterestCategory(metric),
        this.#tikTokCategory(metric),
        this.#twitterCategory(metric),
        this.#tradedeskCategory(metric),
        this.#facebookCategory(metric),
        this.#subscriptionCategory(metric),
        this.#ltvCategory(metric),
        this.#forecastedNorthbeamCategory(metric),
        MetricCategory.NORTHBEAM,
      ].filter(Boolean);

      return categorizers[0];
    }) as MetricCategories;

    if (groupLikeMetrics) {
      return Object.entries(groupedByCategoryMetrics).reduce(
        (acc, [category, metrics]) => ({
          ...acc,
          [category as MetricCategory]: this.#groupLikeMetrics(metrics),
        }),
        {} as MetricCategories,
      );
    }

    return groupedByCategoryMetrics;
  }

  #groupLikeMetrics(metrics: SalesPageMetric[]) {
    const groupedMetrics = groupBy(metrics, ({ metadata }) => {
      const metricId = metadata?.metricId;
      const ltv = metadata?.isLtv ? "ltv" : "";
      return `${ltv}${metricId}`;
    });

    const groupedMetricsArray = Object.entries(groupedMetrics).reduce<
      SalesPageMetricWithChildren[]
    >((acc, [group, metrics]) => {
      if (group === "undefined") {
        return [...acc, ...metrics];
      }

      const parentMetric = metrics.find(
        ({ metadata }) => metadata?.type === "",
      );
      if (parentMetric) {
        const metricChildren = [
          ...metrics.filter(({ metadata }) => metadata?.type !== ""),
        ];
        (parentMetric as SalesPageMetricWithChildren).children = orderBy(
          metricChildren,
          "name",
        );
        parentMetric.name = parentMetric.metadata?.name ?? parentMetric.name;
        return [...acc, parentMetric];
      }

      return [...acc, ...metrics];
    }, []);

    return orderBy(groupedMetricsArray, "name");
  }

  #forecastedNorthbeamCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("forecasted")) {
      return MetricCategory.FORECASTED_NORTHBEAM;
    }
    return "";
  }

  #ltvCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("ltv")) {
      return MetricCategory.LTV;
    }
    return "";
  }

  #userDefinedCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.composition.type === CreativeMetricType.user_defined) {
      return MetricCategory.USER_DEFINED;
    }

    return "";
  }

  #subscriptionCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("subscription")) {
      return MetricCategory.SUBSCRIPTION;
    }
    return "";
  }

  #facebookCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("fb ")) {
      return MetricCategory.FACEBOOK;
    }
    return "";
  }

  #tikTokCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.composition.type === "base") {
      return "";
    } else if (metric.name.toLowerCase().includes("tiktok ")) {
      return MetricCategory.TIK_TOK;
    }
    return "";
  }

  #googleCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("google ")) {
      return MetricCategory.GOOGLE;
    }
    return "";
  }

  #pinterestCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("pinterest")) {
      return MetricCategory.PINTEREST;
    }
    return "";
  }

  #twitterCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("twitter")) {
      return MetricCategory.TWITTER;
    }
    return "";
  }

  #tradedeskCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.spec?.group.toLowerCase().includes("tradedesk")) {
      return MetricCategory.TRADEDESK;
    }
    return "";
  }

  #youTubeCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("youtube")) {
      return MetricCategory.YOU_TUBE;
    }
    return "";
  }

  #snapchatCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("snapchat ")) {
      return MetricCategory.SNAPCHAT;
    }
    return "";
  }

  #customGoalCategory(metricSpec: SalesPageMetric): MetricCategory | "" {
    if (metricSpec.metadata.isGoalRelated) {
      return MetricCategory.CUSTOM_GOALS;
    }
    return "";
  }

  #platformBenchmarksCategory(metric: SalesPageMetric): MetricCategory | "" {
    if (metric.name.toLowerCase().includes("benchmark")) {
      return MetricCategory.PLATFORM_BENCHMARKS;
    }
    return "";
  }
}
